import { MapPinIcon, UsersIcon } from '@heroicons/react/20/solid';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Listbox } from '@headlessui/react';
import { Container } from '../common/Container';

export function Jobs({ jobs }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  const suggestionsRef = useRef(null);

  const states = useMemo(() => {
    return [...new Set(jobs.map((job) => job.state.name))];
  }, [jobs]);

  const departments = useMemo(() => {
    return [...new Set(jobs.map((job) => job.department.name))];
  }, [jobs]);

  const sortedJobs = useMemo(() => {
    return [...jobs].sort((a, b) => a.title.localeCompare(b.title));
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    return sortedJobs.filter((job) => {
      const jobTitleLower = job.title.toLowerCase();
      const searchTermLower = searchTerm.toLowerCase();

      const matchesJobTitle = jobTitleLower.startsWith(searchTermLower) || jobTitleLower.includes(searchTermLower);

      const matchesState = job.is_national_job ? true : (selectedState ? job.state.name === selectedState : true);
      const matchesDepartment = selectedDepartment
        ? job.department.name === selectedDepartment
        : true;
      return matchesJobTitle && matchesState && matchesDepartment;
    });
  }, [sortedJobs, searchTerm, selectedState, selectedDepartment]);

  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);

  const currentJobs = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredJobs.slice(indexOfFirstItem, indexOfLastItem);
  }, [currentPage, itemsPerPage, filteredJobs]);

  const generatePageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 3;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      if (startPage > 1) {
        pageNumbers.push(1, '...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages) {
        pageNumbers.push('...', totalPages);
      }
    }

    return pageNumbers;
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [itemsPerPage, currentPage, totalPages]);

  useEffect(() => {
    const jobTitles = jobs.map((job) => job.title);
    const uniqueJobTitles = Array.from(new Set(jobTitles.map((title) => title.toLowerCase())));
    setSuggestions(uniqueJobTitles);
  }, [jobs]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (event) => {
    const newSearchTerm = event.target.value.toLowerCase();
    setSearchTerm(newSearchTerm);
    setShowSuggestions(true);

    // Filtrar sugestões para títulos cuja primeira letra começa com a letra digitada
    setFilteredSuggestions(
      suggestions.filter((suggestion) => {
        return suggestion.toLowerCase().startsWith(newSearchTerm);
      }),
    );
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.title);
    setShowSuggestions(false);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedState('');
    setSelectedDepartment('');
  };

  return (
    <Container>
      <Container1>
        <div className="relative lg:grid lg:grid-cols-3 lg:gap-[45px] py-4">
          <div className='h-full'>
            <div className="flex flex-col items-center justify-start h-full col-span-1 mb-8">
              <div className="flex flex-col items-center justify-start w-full gap-4 lg:sticky lg:top-0">
                <div className="w-full">
                  <h3 className="font-poppins font-normal text-xl mb-[13px]">Filtros</h3>
                  <div className="border-b-2 mb-[27px]"></div>
                </div>
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Pesquise por vaga..."
                    className="w-full px-4 py-2 truncate border-none rounded-lg shadow-md appearance-none"
                    value={searchTerm}
                    onChange={handleInputChange}
                  />
                  <div className="absolute top-2 right-3 ">
                    <MagnifyingGlassIcon className="w-5 h-5 text-primary" />
                  </div>
                  {showSuggestions && (
                    <ul
                      ref={suggestionsRef}
                      className="absolute z-10 w-full mt-2 max-h-[200px] overflow-y-auto  bg-white border rounded-md shadow-md"
                    >
                      {filteredJobs.map((job, index) => (
                        <li
                          key={index}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-200"
                          onClick={() => handleSuggestionClick(job)}
                        >
                          {job.title}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="w-full">
                  <Listbox value={selectedState} onChange={setSelectedState}>
                    <div className="">
                      <label htmlFor="state">Estado:</label>
                      <Listbox.Button
                        id="state"
                        className="relative w-full px-4 py-2 text-left text-gray-700 bg-white border-none rounded-lg shadow-md appearance-none"
                      >
                        {selectedState || 'Todos os estados'}
                        <span className="absolute flex items-center pr-2 pointer-events-none top-3 right-3">
                          <ChevronDownIcon className="w-5 h-5 text-primary" aria-hidden="true" />
                        </span>
                      </Listbox.Button>
                    </div>
                    <Listbox.Options className="absolute z-50 w-full max-w-xl py-1 mt-1 text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {states.map((state, index) => (
                        <Listbox.Option key={index} value={state}>
                          {({ active }) => (
                            <li
                              className={`${
                                active ? 'bg-gray-500' : 'bg-white text-black'
                              } px-4 py-6 cursor-pointer hover:bg-gray-200`}
                            >
                              {state}
                            </li>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Listbox>
                </div>
                <div className="w-full">
                  <Listbox value={selectedDepartment} onChange={setSelectedDepartment}>
                    <div className="">
                      <label htmlFor="departments">Departamentos:</label>
                      <Listbox.Button
                        id="departments"
                        className="relative w-full px-4 py-2 text-left text-gray-700 bg-white border-none rounded-lg shadow-md appearance-none"
                      >
                        {selectedDepartment || 'Todos os departamentos'}
                        <span className="absolute flex items-center pr-2 pointer-events-none top-3 right-3">
                          <ChevronDownIcon className="w-5 h-5 text-primary" aria-hidden="true" />
                        </span>
                      </Listbox.Button>
                    </div>
                    <Listbox.Options className="absolute z-50 w-full max-w-xl py-1 mt-1 text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {departments.map((department, index) => (
                        <Listbox.Option key={index} value={department}>
                          {({ active }) => (
                            <li
                              className={`${
                                active ? 'bg-gray-500' : 'bg-white text-black'
                              } px-4 py-2 cursor-pointer hover:bg-gray-200`}
                            >
                              {department}
                            </li>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Listbox>
                  <div className="w-full mt-2">
                    <button
                      onClick={clearFilters}
                      disabled={!searchTerm && !selectedState && !selectedDepartment}
                      className={`w-full underline ${
                        !searchTerm && !selectedState && !selectedDepartment
                          ? ' text-gray-500 cursor-not-allowed'
                          : 'text-primary'
                      }`}
                    >
                      Limpar Filtros
                    </button>
                  </div>
                  <div className="flex justify-center w-full mt-4">
                    {/* <button onClick={() => paginate(1)} disabled={currentPage === 1} className="px-2 py-1 mr-2">
                      <ChevronDoubleLeftIcon className="w-4 h-4 text-primary" />
                    </button> */}
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`${totalPages < 2 && 'hidden'} flex justify-center items-center h-12 w-12 rounded-lg border border-gray-500 hover:scale-105 transition-all duration-300 ease-in-out`}
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    {generatePageNumbers().map((number, index) => (
                      <button
                        key={index}
                        className={`${
                          number === currentPage ? 'font-bold' : 'text-gray-400 font-light'
                        } hover:bg-gray-200 px-2 py-1 mx-1 `}
                        onClick={() => (typeof number === 'number' ? paginate(number) : null)}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className={`${totalPages < 2 && 'hidden'} flex justify-center items-center h-12 w-12 rounded-lg border border-gray-500 hover:scale-105 transition-all duration-300 ease-in-out`}
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-6 md:col-span-2 md:grid md:grid-rows-3 md:grid-cols-2">
            {currentJobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        </div>
      </Container1>
    </Container>
  );
}

function Container1({ children }) {
  return (
    <div className="py-4 mt-10 min-h-[800px]">
      <div className="max-w-4xl px-4 mx-auto sm:px-6 lg:max-w-full lg:px-8">
        <div className="relative pt-12 pb-6 sm:pb-6 xl:col-start-1 xl:pb-4">
          <div className="flex flex-col items-center justify-center">
            <div>
              <h2
                id="jobs"
                className="relative text-2xl font-medium text-center text-black font-montserrat md:text-3xl lg:text-4xl bg-gradient-to-r bg-clip-text"
              >
                Veja nossas vagas
              </h2>
              <div className="w-1/2 mx-auto border-b-2 border-primary"></div>
            </div>
          </div>
          <div className="mt-12 gap-y-12 gap-x-6 ">{children}</div>
        </div>
      </div>
    </div>
  );
}

function JobCard({ job }) {
  return (
    <div className="relative flex flex-col justify-between shadow-lg border min-h-[223px] rounded-[22px] px-[30px] max-w-[456px] w-full">
      <div className="z-10 absolute -top-4 bg-white right-0 h-[25px] border-black border rounded-full px-[18px]">
        <p className="font-medium font-montserrat">{job.hiring_policy}</p>
      </div>

      <div className="absolute h-full top-[30px] bottom-[30px] right-[30px] left-[30px]">
        <div className="flex flex-col justify-between h-[107px]">
          <div>
            <h2 className="font-bold font-montserrat text-2xl max-h-[29px] overflow-hidden whitespace-nowrap truncate">
              <abbr title={job?.title} className="no-underline">
                {job?.title}
              </abbr>
            </h2>
            <h3 className="truncate">
              <abbr className="no-underline" title={`${job?.is_national_job ? "Brasil" : `${job?.city?.name} - ${job?.state?.name}`}`}>
                {job?.is_national_job ? "Brasil" : `${job?.city?.name} - ${job?.state?.name}`}
              </abbr>
            </h3>
          </div>

          <div>
            <p className="truncate">
              <span className="text-xl font-medium uppercase font-montserrat">Cargo do Tipo: </span>
              <span className="text-sm font-montserrat">
                <abbr title={job?.department?.name} className="no-underline">
                  {job?.department?.name}
                </abbr>
              </span>
            </p>
          </div>
        </div>

        <div className="mt-[15px]">
          <a href={job?.url} target="_blank">
            <button className="text-sm md:text-base flex h-[41px] justify-center items-center w-full bg-primary text-white py-3 rounded-full hover:scale-105 transition-all duration-300">
              Quero me candidatar
            </button>
          </a>
        </div>
      </div>
    </div>
  );
}
